<template>
  <div class="rc" :class="{ 'rc--open': open, 'rc--error': !!flag }" data-test="workflow-rule">
    <div class="rc__head">
      <button
        type="button"
        class="rc__toggle"
        :aria-expanded="open ? 'true' : 'false'"
        data-test="workflow-rule-toggle"
        @click="$emit('toggle')"
      >
        <WfIcon name="chevR" :size="16" class="rc__chev" />
        <span class="rc__titles">
          <span class="rc__title-row">
            <span class="rc__title" data-test="workflow-rule-title">{{ title }}</span>
            <span v-if="flag" class="rc__badge rc__badge--error" data-test="workflow-rule-flag">{{ flag }}</span>
            <span v-if="empty" class="rc__badge" data-test="workflow-rule-notset">Not set up</span>
          </span>
          <span v-if="!open && summary" class="rc__sum" data-test="workflow-rule-summary">{{ summary }}</span>
        </span>
      </button>
      <button
        type="button"
        class="rc__remove"
        :aria-label="`Remove ${title.toLowerCase()}`"
        data-test="workflow-rule-remove"
        @click="$emit('remove')"
      >
        <WfIcon name="trash" :size="16" />
      </button>
    </div>
    <div v-if="open" class="rc__body" data-test="workflow-rule-body">
      <slot />
    </div>
  </div>
</template>

<script>
/**
 * One rule of a settings panel as a card (design frames 1g / 1h): a chevron, "Rule 1", the one-line
 * summary while folded, a "Not set up" badge on an empty rule, and a remove button. Open, it shows the
 * rule's rows (the default slot).
 *
 * A rule with a problem the caller has found (a text too long to send) passes `flag`: the card gets a red
 * border and a red badge with that label beside the title, so the problem shows folded AND open.
 *
 * The caller owns the fold state (settings/ruleFold.js) and the summary text (src/utils/workflow/
 * ruleSummary.js). ⛔ The card never clips: nothing here cuts its content, so a picker's list can open over
 * the cards below it.
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings / isSuperAdmin / userID.
 */
import WfIcon from '../WfIcon.vue';

export default {
  name: 'RuleCard',
  components: { WfIcon },
  props: {
    title: { type: String, required: true },
    summary: { type: String, default: '' },
    /** Nothing but the defaults is set: shows the "Not set up" badge. */
    empty: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
    /** A problem label ('Too long to send'): red border and a red badge. '' = no problem. */
    flag: { type: String, default: '' },
  },
};
</script>

<style lang="scss" scoped>
@import '../workflow-tokens';

.rc {
  @include fu-card(14px);
  box-shadow: $fu-shadow-xs;
}

// A flagged rule (a text too long to send): red border, in step with the red badge below.
.rc--error { border-color: $fu-danger; }

.rc__head {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 0 10px 0 0;
}

.rc__toggle {
  @include fu-button-reset;
  @include fu-focus-ring;
  flex: 1 1 auto;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  padding: 13px 4px 13px 14px;
  border-radius: 14px;
  font-family: inherit;
  user-select: none;

  &:hover { background: $fu-neutral-25; }
}

.rc__chev {
  margin-top: 3px;
  color: $fu-neutral-500;
  transition: transform $fu-dur-fast $fu-ease-out;
}

.rc--open .rc__chev { transform: rotate(90deg); }

.rc__titles {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 1px;
}

.rc__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.rc__title {
  font-size: 14.5px;
  font-weight: 800;
  line-height: 1.3;
  color: $fu-text;
}

.rc__badge {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: $fu-warning-subtle;
  color: $fu-warning-text;
  font-size: 11.5px;
  font-weight: 700;

  &--error {
    background: $fu-danger-subtle;
    color: $fu-danger-text;
  }
}

.rc__sum {
  display: block;
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.45;
  color: $fu-neutral-600;
  overflow-wrap: anywhere;
}

.rc__remove {
  @include fu-icon-btn(32px);
  margin-top: 6px;

  @include wf-touch {
    width: 44px;
    height: 44px;
    margin-top: 0;
  }
}

.rc__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 20px 20px;
  border-top: 1px solid $fu-neutral-100;
}
</style>
